import type { Term, Week } from "@ts/interfaces/terms/api/termsApiInterface";
import type {
  GradeStructureApiResponse,
  StructureTerm,
  StructureSubject,
  StructureWeek
} from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type {
  GradeProgressApiResponse,
  ProgressTerm,
  ProgressWeek
} from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import type { AssignmentTermSummary } from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import { COMPLETION_STATUS } from "@ts/enums/CompletionStatus";

/**
 * Returns value when it is an array, otherwise []. Guards the merge functions
 * against a malformed API body (e.g. a 5xx error envelope where an expected
 * array field is an object/string) reaching .map()/.find() and crashing the
 * page. See: BLD2-806.
 */
export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

const EMPTY_ASSIGNMENTS = {
  total: 0,
  total_completed: 0,
  total_exempted: 0,
  url: ""
};

function weekCounts(progressWeek: ProgressWeek | undefined) {
  return {
    total: progressWeek?.lessons_total ?? 0,
    total_completed: progressWeek?.lessons_completed ?? 0
  };
}

// Rebuilds the legacy Term[] from the split endpoints. assignmentsUrl is passed
// in from dashboard-details — it is deliberately not on term_summary.
export function mergeTerms(
  structure: GradeStructureApiResponse | null,
  progress: GradeProgressApiResponse | null,
  termSummary: Record<string, AssignmentTermSummary> | null,
  assignmentsUrl: string
): Term[] {
  if (!structure) return [];

  const progressByTerm = new Map<number, ProgressTerm>();
  for (const t of asArray<ProgressTerm>(progress?.terms)) {
    progressByTerm.set(t.id, t);
  }

  const structureTerms = asArray<StructureTerm>(structure.terms);

  return structureTerms.map(structureTerm => {
    const progressTerm = progressByTerm.get(structureTerm.id);

    const progressWeekById = new Map<number, ProgressWeek>();
    for (const w of asArray<ProgressWeek>(progressTerm?.weeks)) {
      progressWeekById.set(w.id, w);
    }

    // Weeks are nested under subjects in structure; the legacy Term.weeks is a
    // flat per-term list. Collect distinct week id+name across the term's
    // subjects, ordered by week id.
    const weekMeta = new Map<number, string>();
    for (const subject of asArray<StructureSubject>(structureTerm.subjects)) {
      for (const week of asArray<StructureWeek>(subject.weeks)) {
        if (!weekMeta.has(week.id)) weekMeta.set(week.id, week.name);
      }
    }

    const weeks: Week[] = [...weekMeta.entries()]
      .sort(([a], [b]) => a - b)
      .map(([id, name]) => ({
        id: String(id),
        name,
        lessons: weekCounts(progressWeekById.get(id))
      }));

    const summary = termSummary?.[String(structureTerm.id)];

    return {
      id: String(structureTerm.id),
      name: structureTerm.name,
      status: progressTerm?.status ?? COMPLETION_STATUS.NOT_STARTED,
      has_active_entitlement: asArray<StructureSubject>(
        structureTerm.subjects
      ).some(s => s.has_active_entitlement),
      lessons: {
        total: progressTerm?.lessons_total ?? 0,
        total_completed: progressTerm?.lessons_completed ?? 0
      },
      weeks,
      assignments: summary
        ? {
            total: summary.total,
            total_completed: summary.total_completed,
            total_exempted: summary.total_exempted,
            url: assignmentsUrl
          }
        : { ...EMPTY_ASSIGNMENTS, url: assignmentsUrl }
    };
  });
}
